/**
 * BrainStatusTile — one tile in the L1 brain-status grid.
 *
 * Layout exactly matches spec §4.3:
 *
 *   ┌─────────────────────────┐
 *   │ KNOWLEDGE          ●    │
 *   │                         │
 *   │ 12 organs · 2,431 wkrs  │
 *   │ 14 decisions / 1m       │
 *   │                         │
 *   │ [42 refusals · 3 alerts]│
 *   └─────────────────────────┘
 *
 * Click → drills to /omega/brain/:id. Hover affordance is a 1-px
 * accent rim swap (no shadow, no scale, no fade transitions
 * exceeding 200 ms).
 */

import { Link } from 'react-router-dom';

import { StatusDot } from '../atoms/StatusDot';
import type { BrainSummary } from '../data/types';
import { getBrain } from '../data/brainRegistry';

export function BrainStatusTile({ brain }: { brain: BrainSummary }) {
  const entry = getBrain(brain.id);
  const display = entry?.display_name ?? brain.id;
  const organCount = brain.organ_count ?? 0;
  const workerCount = brain.worker_count ?? 0;
  const decisionsLastMin = brain.decisions_last_min ?? 0;
  const refusalsLastMin = brain.refusals_last_min ?? 0;
  const activeAlerts = brain.active_alerts ?? 0;
  const specialists = brain.bound_specialists ?? [];

  return (
    <Link
      to={`/omega/brain/${brain.id}`}
      aria-label={`Open ${display} brain — status ${brain.status}`}
      style={{
        display: 'block',
        textDecoration: 'none',
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-2)',
        color: 'var(--omega-ink)',
        transition: 'border-color 180ms ease-out',
        minHeight: 132,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--omega-accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--omega-rim)';
      }}
    >
      {/* Header: NAME … status dot */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <span
          style={{
            fontSize: '12pt',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--omega-ink)',
          }}
        >
          {display}
        </span>
        <span style={{ flex: 1 }} />
        <StatusDot
          status={brain.status}
          pulse={brain.status === 'critical'}
        />
      </div>

      {/* organs · workers */}
      <div
        style={{
          fontSize: '11pt',
          color: 'var(--omega-ink-muted)',
          letterSpacing: '0.02em',
        }}
      >
        <span className="omega-mono" style={{ color: 'var(--omega-ink)' }}>
          {organCount}
        </span>{' '}
        organ{organCount === 1 ? '' : 's'}
        <span style={{ margin: '0 8px', color: 'var(--omega-ink-subtle)' }}>
          ·
        </span>
        <span className="omega-mono" style={{ color: 'var(--omega-ink)' }}>
          {workerCount.toLocaleString()}
        </span>{' '}
        worker{workerCount === 1 ? '' : 's'}
      </div>

      {/* decisions / 1m */}
      <div
        style={{
          fontSize: '10pt',
          color: 'var(--omega-ink-muted)',
          marginTop: 4,
        }}
      >
        <span className="omega-mono" style={{ color: 'var(--omega-ink)' }}>
          {decisionsLastMin}
        </span>{' '}
        decision{decisionsLastMin === 1 ? '' : 's'} / 1m
      </div>

      {/* Bound specialists — Phase 7 */}
      {specialists.length > 0 && (
        <div
          style={{
            marginTop: 4,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          {specialists.map((s) => (
            <span
              key={s.id}
              style={{
                fontSize: '8pt',
                padding: '1px 5px',
                border: '1px solid var(--omega-rim)',
                color: s.status === 'live'
                  ? 'var(--omega-state-healthy)'
                  : 'var(--omega-ink-muted)',
                letterSpacing: '0.04em',
              }}
            >
              {s.id}
            </span>
          ))}
        </div>
      )}

      {/* Bottom strip: refusals · alerts. Sticks to the bottom even
          when the middle content is short, so all 6 tiles share a
          baseline. */}
      <div
        style={{
          marginTop: 'var(--omega-gap-2)',
          paddingTop: 'var(--omega-gap-1)',
          borderTop: '1px solid var(--omega-rim)',
          display: 'flex',
          gap: 'var(--omega-gap-2)',
          fontSize: '9pt',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        <span
          style={{
            color:
              refusalsLastMin > 0
                ? 'var(--omega-state-refused)'
                : 'var(--omega-ink-subtle)',
          }}
        >
          {refusalsLastMin} refusals
        </span>
        <span style={{ color: 'var(--omega-ink-subtle)' }}>·</span>
        <span
          style={{
            color:
              activeAlerts > 0
                ? 'var(--omega-state-degraded)'
                : 'var(--omega-ink-subtle)',
          }}
        >
          {activeAlerts} alerts
        </span>
      </div>
    </Link>
  );
}
